import { useState, useEffect, useCallback } from "react";
import API, { formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus, Pencil, Trash2, Loader2, Package, Upload, ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const emptyProduct = { product_code: "", name: "", category: "", sub_category: "", instrument_codes: [], pack_size: "", mrp: 0, customer_price: 0, reagent_type: "open", description: "", is_featured: false };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState({}); // { [product_code]: true }
  const [uploadMsg, setUploadMsg] = useState({}); // { [product_code]: { type, text } }
  const [allInstruments, setAllInstruments] = useState([]); // [{instrument_code, segment}]
  const [instFilter, setInstFilter] = useState("");

  useEffect(() => {
    API.get("/admin/instruments").then(r => setAllInstruments(r.data || [])).catch(() => setAllInstruments([]));
  }, []);

  const toggleInstrument = (code) => {
    setForm(f => {
      const cur = Array.isArray(f.instrument_codes) ? f.instrument_codes : [];
      return { ...f, instrument_codes: cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code] };
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set("search", search);
      const { data } = await API.get(`/products?${params}`);
      setProducts(data.products || []);
      setTotalPages(data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const flash = (code, type, text) => {
    setUploadMsg(prev => ({ ...prev, [code]: { type, text } }));
    setTimeout(() => setUploadMsg(prev => { const n = { ...prev }; delete n[code]; return n; }), 3000);
  };

  const uploadImage = async (code, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { flash(code, "error", "Only images allowed"); return; }
    setUploading(prev => ({ ...prev, [code]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      await API.post(`/admin/products/${encodeURIComponent(code)}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      flash(code, "success", "Image uploaded");
      load();
    } catch (e) {
      flash(code, "error", e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(prev => { const n = { ...prev }; delete n[code]; return n; });
    }
  };

  const openCreate = () => { setForm(emptyProduct); setEditing(null); setError(""); setInstFilter(""); setShowForm(true); };
  const openEdit = (p) => {
    const codes = Array.isArray(p.instrument_codes) && p.instrument_codes.length
      ? p.instrument_codes
      : (p.instrument_code ? [p.instrument_code] : []);
    setForm({ ...p, instrument_codes: codes });
    setEditing(p.product_code); setError(""); setInstFilter(""); setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      if (editing) {
        await API.put(`/admin/products/${editing}`, form);
      } else {
        await API.post("/admin/products", form);
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete product ${code}?`)) return;
    try {
      await API.delete(`/admin/products/${code}`);
      load();
    } catch {}
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="admin-products">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Products</h2>
          <p className="text-sm text-gray-500 mt-0.5">Manage reagent kit catalog</p>
        </div>
        <Button onClick={openCreate} className="bg-[#008A99] hover:bg-[#006670] text-white rounded-xl shadow-lg shadow-[#008A99]/20" data-testid="add-product-button">
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input data-testid="product-search-admin" placeholder="Search products..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-10 h-10 rounded-xl" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Image</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Code</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Category</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Type</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">MRP</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">Cust. Price</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {products.map((p) => {
                  const isUp = !!uploading[p.product_code];
                  const msg = uploadMsg[p.product_code];
                  const imgSrc = p.image_url ? (p.image_url.startsWith("http") ? p.image_url : `${BACKEND_URL}${p.image_url}`) : "";
                  return (
                  <tr key={p.product_code} className="hover:bg-slate-50/50" data-testid={`product-row-${p.product_code}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-12 h-12 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {imgSrc ? (
                            <img src={imgSrc} alt={p.product_code} className="w-full h-full object-contain" onError={e => { e.target.style.display = 'none'; }} data-testid={`product-image-${p.product_code}`} />
                          ) : (
                            <ImageIcon className="w-4 h-4 text-slate-300" />
                          )}
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <label className={`text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-md border cursor-pointer ${isUp ? "border-slate-200 text-slate-400" : "border-[#008A99]/40 text-[#008A99] hover:bg-[#008A99]/5"}`}>
                            {isUp ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            {imgSrc ? "Replace" : "Upload"}
                            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={isUp}
                              onChange={e => { const f = e.target.files[0]; if (f) uploadImage(p.product_code, f); e.target.value = ""; }}
                              data-testid={`upload-product-image-${p.product_code}`} />
                          </label>
                          {msg && (
                            <span className={`text-[9px] font-medium ${msg.type === "success" ? "text-emerald-600" : "text-red-500"}`} data-testid={`upload-msg-${p.product_code}`}>{msg.text}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.product_code}</td>
                    <td className="px-4 py-3 font-medium text-[#0F172A]">{p.name}</td>
                    <td className="px-4 py-3 text-slate-600">{p.category}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${p.reagent_type === "open" ? "bg-emerald-50 text-emerald-600" : "bg-sky-50 text-sky-600"}`}>{p.reagent_type}</span></td>
                    <td className="px-4 py-3 text-right font-semibold">Rs. {p.mrp?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-semibold text-[#008A99]" data-testid={`cp-${p.product_code}`}>{p.customer_price ? `Rs. ${p.customer_price?.toLocaleString()}` : <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-1">
                        <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`edit-${p.product_code}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(p.product_code)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" data-testid={`delete-${p.product_code}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );})}
                {products.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">No products found</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Product Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          {error && <div className="p-2 bg-red-50 text-red-600 text-sm rounded-lg">{error}</div>}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">MRP (Rs.)</label>
                <Input data-testid="form-mrp" type="number" value={form.mrp} onChange={(e) => setForm(f => ({ ...f, mrp: parseFloat(e.target.value) || 0 }))} className="h-9 rounded-lg" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Customer Price (Rs.)</label>
                <Input data-testid="form-customer-price" type="number" value={form.customer_price || 0} onChange={(e) => setForm(f => ({ ...f, customer_price: parseFloat(e.target.value) || 0 }))} className="h-9 rounded-lg" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Product Code</label>
              <Input data-testid="form-product-code" value={form.product_code} onChange={(e) => setForm(f => ({ ...f, product_code: e.target.value }))} disabled={!!editing} className="h-9 rounded-lg" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Product Name</label>
              <Input data-testid="form-product-name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 rounded-lg" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Category</label>
                <Input data-testid="form-category" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="h-9 rounded-lg" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Sub Category</label>
                <Input data-testid="form-sub-category" value={form.sub_category} onChange={(e) => setForm(f => ({ ...f, sub_category: e.target.value }))} className="h-9 rounded-lg" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">
                Instrument(s) <span className="text-[#008A99] font-semibold">— required for the product to appear in the catalog</span>
              </label>
              <div className="border border-slate-200 rounded-lg p-2" data-testid="form-instruments">
                <div className="flex flex-wrap gap-1 mb-2 min-h-[24px]">
                  {(form.instrument_codes || []).length === 0
                    ? <span className="text-[11px] text-slate-400">No instrument selected yet</span>
                    : (form.instrument_codes || []).map(c => (
                      <span key={c} className="inline-flex items-center gap-1 text-[11px] bg-[#008A99]/10 text-[#008A99] px-2 py-0.5 rounded-full" data-testid={`form-instrument-chip-${c}`}>
                        {c}
                        <button type="button" onClick={() => toggleInstrument(c)} className="hover:text-red-500">×</button>
                      </span>
                    ))}
                </div>
                <Input placeholder="Filter instruments..." value={instFilter} onChange={(e) => setInstFilter(e.target.value)} className="h-8 rounded-lg mb-1 text-xs" data-testid="form-instrument-filter" />
                <div className="max-h-36 overflow-y-auto space-y-0.5">
                  {allInstruments
                    .filter(i => {
                      const q = instFilter.trim().toLowerCase();
                      if (!q) return true;
                      return (i.instrument_code || "").toLowerCase().includes(q) || (i.segment || "").toLowerCase().includes(q);
                    })
                    .map(i => {
                      const checked = (form.instrument_codes || []).includes(i.instrument_code);
                      return (
                        <label key={i.instrument_code} className={`flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer text-xs ${checked ? "bg-[#008A99]/5" : "hover:bg-slate-50"}`} data-testid={`form-instrument-option-${i.instrument_code}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleInstrument(i.instrument_code)} className="accent-[#008A99]" />
                          <span className="font-medium text-slate-700">{i.instrument_code}</span>
                          {i.segment && <span className="text-[10px] text-slate-400">· {i.segment}</span>}
                        </label>
                      );
                    })}
                  {allInstruments.length === 0 && <p className="text-[11px] text-slate-400 px-2 py-1">No instruments configured yet.</p>}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Pack Size</label>
                <Input data-testid="form-pack-size" value={form.pack_size} onChange={(e) => setForm(f => ({ ...f, pack_size: e.target.value }))} className="h-9 rounded-lg" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Reagent Type</label>
                <select data-testid="form-reagent-type" value={form.reagent_type} onChange={(e) => setForm(f => ({ ...f, reagent_type: e.target.value }))} className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm">
                  <option value="open">Open Reagent</option>
                  <option value="close">Closed Reagent</option>
                  <option value="qc_cal">QC & Calibrators</option>
                  <option value="elisa">ELISA</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Description</label>
              <textarea data-testid="form-description" value={form.description || ""} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={3}
                placeholder="Brief product description, scope or application…"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-y focus:outline-none focus:border-[#008A99]" />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input data-testid="form-is-featured" id="is_featured" type="checkbox" checked={!!form.is_featured}
                onChange={(e) => setForm(f => ({ ...f, is_featured: e.target.checked }))}
                className="w-4 h-4 rounded accent-[#008A99]" />
              <label htmlFor="is_featured" className="text-xs text-slate-600 font-medium select-none cursor-pointer">
                Featured in <span className="text-[#008A99] font-semibold">New Launch</span> section
              </label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#008A99] hover:bg-[#006670] text-white rounded-xl" data-testid="save-product-button">
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
